import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  DataTable,
  PageHeader,
  Input,
  SpinnerLoader,
  Toastify,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { Edit, RefreshCw, Search } from 'lucide-react';
import { debounce } from '@lift-admin/utils/debounce';
import { useThrottledFn } from '@lift-admin/utils/throttle';
import apiClient from '@lift-admin/utils/apiClient';
import { useAuthContext } from '@lift-admin/utils/provider';
import ShowPub from './components/show-pub';

const PubMapping = () => {
  const { canEdit } = useAuthContext();
  const hasEditAccess = canEdit('affiliate_mgmt');

  const [brandPub, setBrandPub] = useState<any[]>([]);
  const [brandPubBackup, setBrandPubBackup] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');
  const [listLoad, setListLoad] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [showEditBrandPubDialog, setShowEditBrandPubDialog] = useState(false);
  const [brandInfo, setBrandInfo] = useState<any>(null);
  const [brandInfoChange, setBrandInfoChange] = useState(false);

  const customSort = (a: any, b: any) => a.brand_id - b.brand_id;

  const getBrandPubMapping = () => {
    setListLoad(true);
    apiClient
      .get(`/affcrm/v1/admin/brand/pub`)
      .then((res: any) => {
        setListLoad(false);
        const data = res.data.response || [];
        data.sort(customSort);
        setBrandPub(data);
        setBrandPubBackup(data);
        setPageIndex(0);
      })
      .catch((err: any) => {
        setListLoad(false);
        Toastify('error', err.response?.data?.message || 'Failed to fetch brand publisher mapping');
        console.error(err);
      });
  };

  const throttleGetBrandPubMapping = useThrottledFn(getBrandPubMapping, 1000);

  // Search results
  const searchResults = (query: any) => {
    let results: any = [];
    for (let i = 0; i < brandPubBackup.length; i++) {
      if (
        brandPubBackup[i]['brand']?.toLowerCase().includes(query.toLowerCase())
      )
        results.push(brandPubBackup[i]);
    }
    setBrandPub(results);
  };

  const searchOptimized = useCallback(debounce(searchResults, 600), [
    brandPubBackup,
  ]);

  useEffect(() => {
    getBrandPubMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandInfoChange]);

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return brandPub.slice(start, start + pageSize);
  }, [brandPub, pageIndex, pageSize]);

  const dataTableColumns = useMemo(() => {
    const cols: TableColumn<any>[] = [
      {
        key: 'brand',
        header: 'Brand Name',
        accessor: 'brand',
        render: (_v: any, row: any) => <div className="font-medium">{row.brand}</div>,
      },
      {
        key: 'publishers',
        header: 'Not Selected Publishers',
        accessor: (row: any) => row.publishers || '',
        render: (_v: any, row: any) => {
          const publishers = row.publishers || '';
          return (
            <div>
              {publishers.length !== 0
                ? publishers.length > 60
                  ? publishers.substring(0, 60) + '...'
                  : publishers
                : 'Not Set'}
            </div>
          );
        },
      },
    ];
    if (hasEditAccess) {
      cols.push({
        key: 'actions',
        header: 'Actions',
        accessor: (row: any) => row.brand_id ?? row.id,
        width: '100px',
        align: 'center' as const,
        render: (_v: any, row: any) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setBrandInfo(row);
              setShowEditBrandPubDialog(true);
            }}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
        ),
      });
    }
    return cols;
  }, [hasEditAccess]);

  const admin_data = localStorage.getItem('admin_auth')
    ? JSON.parse(localStorage.getItem('admin_auth') || '')
    : null;

  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader
        title={`Brand Publisher Mapping ${!searchText.trim() ? `(${brandPub.length})` : `(${brandPub.length} of ${brandPubBackup.length})`}`}
        renderCustomTop={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => throttleGetBrandPubMapping()}
              className="flex items-center gap-2 h-8 px-3"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            {admin_data && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="flex items-center gap-2 h-8 px-3"
              >
                <a
                  rel="noreferrer"
                  href={`https://app.lift.lt.partners/testload?_id=${admin_data.user}&token=${admin_data?.token}&onboard=${1}`}
                  target="_blank"
                >
                  Go to LIFT
                </a>
              </Button>
            )}
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search brand publisher mapping..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  searchOptimized(e.target.value);
                }}
                className="pl-10 w-80"
              />
            </div>
          </div>
        }
      />

      <div className="flex-1 flex flex-col min-h-0 px-8 pt-8 pb-4">
        <div className="flex-1 min-h-0">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 170px)"
            columns={dataTableColumns}
            data={paginatedData}
            loading={listLoad}
            emptyMessage={searchText ? 'No brand publisher mapping found matching your search.' : 'No brand publisher mapping found.'}
            stickyHeader
            stickyFooter
            showActions={false}
            pagination={{
              currentPage: pageIndex + 1,
              totalPages: Math.max(1, Math.ceil(brandPub.length / pageSize)),
              pageSize,
              totalItems: brandPub.length,
              onPageChange: (p) => setPageIndex(p - 1),
              onPageSizeChange: (size) => { setPageSize(size); setPageIndex(0); },
              pageSizeOptions: [25, 50, 100, 200],
            }}
          />
        </div>
      </div>

      <ShowPub
        showEditBrandPubDialog={showEditBrandPubDialog}
        setShowEditBrandPubDialog={setShowEditBrandPubDialog}
        brandInfo={brandInfo}
        setBrandInfoChange={setBrandInfoChange}
        brandInfoChange={brandInfoChange}
      />
    </div>
  );
};

export default PubMapping;
